import React, { useState } from 'react';
import { Box, VStack, HStack, Input } from 'native-base';
import { Txt } from '~/shared/Typography';
import { SpIcon } from '~/shared/Icon';
import { faEnvelope, faCircleCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { EmailNotification } from '@springbig/shared';
import { Button } from '~/shared/Theme';
import Pressable from '~/shared/Pressable';
import EmailVerificationModal from '~/shared/EmailVerificationModal';

type NotificationCardProps = {
    notification: EmailNotification;
    onAccept: (id: number, email?: string) => void;
    onDelete: (id: number) => void;
    isAccepting: boolean;
    isDeleting: boolean;
    primary: string;
    email: string;
    lastEmailConfirmationSentAt: string | null;
};

const NotificationCard = (props: NotificationCardProps) => {

    const { notification } = props;

    // Render based on notification type
    if (notification.type === 'pending_confirmation') {
        return <PendingConfirmationCard {...props} />;
    }

    if (notification.type === 'never_miss_message') {
        return <NeverMissMessageCard {...props} />;
    }

    // Default fallback
    return <DefaultNotificationCard {...props} />;
};

// Pending Confirmation Card - for users who already sent confirmation
const PendingConfirmationCard = ({
    notification,
    onAccept,
    onDelete,
    isAccepting,
    primary,
    email,
    lastEmailConfirmationSentAt,
}: NotificationCardProps) => {

    const [showEmailModal, setShowEmailModal] = useState(false);

    const handleEmailSubmit = (trimmedEmail: string) => {
        setShowEmailModal(false);
        // Pass email to onAccept - backend will update member email if different
        onAccept(notification.id, trimmedEmail);
    };

    const handleResend = () => {
        // Call onAccept without email parameter (use existing member email)
        onAccept(notification.id);
    };

    const handleDismiss = () => {
        onDelete(notification.id);
    };

    // Calculate expiry date based on last_email_confirmation_sent_at or notification created_at
    const baseSentAt = lastEmailConfirmationSentAt || notification.created_at;
    const expiresDate = new Date(baseSentAt);
    expiresDate.setDate(expiresDate.getDate() + 3);

    const expiresFormatted = expiresDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    });

    // Calculate actual days left dynamically
    const now = new Date();
    const msLeft = expiresDate.getTime() - now.getTime();
    const daysLeft = Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));

    // Check if email was sent recently (< 1 month ago)
    const isEmailRecentlySent = lastEmailConfirmationSentAt ? (() => {
        const sentAt = new Date(lastEmailConfirmationSentAt);
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        return sentAt > oneMonthAgo;
    })() : false;

    const buttonText = isEmailRecentlySent ? 'Confirmation Email Sent!' : 'Resend Confirmation Email';
    const isButtonDisabled = isAccepting || isEmailRecentlySent;

    return (
        <>
            <Box
                bg="white"
                borderBottomWidth={1}
                borderBottomColor="gray.200"
                px={4}
                py={5}
            >
                <VStack space={4}>
                    <HStack space={3} alignItems="center" justifyContent="space-between">
                        <HStack space={3} alignItems="center" flex={1}>
                            <Box position="relative">
                                <SpIcon icon={faEnvelope} size={48} color={primary} />
                                <Box position="absolute" top={-4} right={-4} bg="white" rounded="full">
                                    <SpIcon icon={faCircleCheck} size={20} color={primary} />
                                </Box>
                            </Box>
                            <VStack flex={1}>
                                <Txt fontSize={18} fontWeight="600" color="gray.900">
                                    {notification.title || 'Pending Email Confirmation'}
                                </Txt>
                                <Txt fontSize={14} color="gray.600" mt={1}>
                                    {notification.text}
                                </Txt>
                            </VStack>
                        </HStack>
                        <Pressable onPress={handleDismiss} p={1}>
                            <SpIcon icon={faXmark} size={24} color="gray.400" />
                        </Pressable>
                    </HStack>

                    {/* Yellow expires banner */}
                    <Box bg="#FFF8E1" p={3} rounded="md">
                        <Txt fontSize={13} color="gray.800" fontWeight="600">
                            Expires in {daysLeft} day{daysLeft === 1 ? '' : 's'} ({expiresFormatted})
                        </Txt>
                        <Txt fontSize={12} color="gray.600" mt={1}>
                            Please click the link in your email before it expires to complete your enrollment.
                        </Txt>
                    </Box>

                    <Button
                        text={buttonText}
                        onPress={handleResend}
                        loading={isAccepting}
                        disabled={isButtonDisabled}
                        variant="button"
                    />

                    <Txt fontSize={13} color="gray.600" textAlign="center" mt={2}>
                        Email sent to: {email}
                    </Txt>
                    <Pressable onPress={() => setShowEmailModal(true)} alignSelf="center">
                        <Txt fontSize={13} color={primary} textDecorationLine="underline">
                            Update email address
                        </Txt>
                    </Pressable>
                </VStack>
            </Box>

            <EmailVerificationModal
                isOpen={showEmailModal}
                onClose={() => setShowEmailModal(false)}
                currentEmail={email}
                onSubmit={handleEmailSubmit}
                isLoading={isAccepting}
            />
        </>
    );
};

// Never Miss Message Card - for first-time email opt-in
const NeverMissMessageCard = ({
    notification,
    onAccept,
    onDelete,
    isAccepting,
    primary,
    email,
}: NotificationCardProps) => {

    const [localEmail, setLocalEmail] = useState(email || '');

    const handleSendConfirmation = () => {
        const trimmedEmail = localEmail.trim();
        // Pass email to onAccept - backend will update member email if different
        // Ensure notification.id is a number
        const notifId = typeof notification.id === 'number' ? notification.id : parseInt(String(notification.id), 10);
        onAccept(notifId, trimmedEmail);
    };

    const handleDismiss = () => {
        onDelete(notification.id);
    };

    return (
        <>
            <Box
                bg="white"
                borderBottomWidth={1}
                borderBottomColor="gray.200"
                px={4}
                py={5}
            >
                <VStack space={4}>
                    <HStack space={3} alignItems="center" justifyContent="space-between">
                        <HStack space={3} alignItems="center" flex={1}>
                            <Box position="relative">
                                <SpIcon icon={faEnvelope} size={48} color={primary} />
                                <Box position="absolute" top={-4} right={-4} bg="white" rounded="full">
                                    <SpIcon icon={faCircleCheck} size={20} color={primary} />
                                </Box>
                            </Box>
                            <VStack flex={1}>
                                <Txt fontSize={18} fontWeight="600" color="gray.900">
                                    {notification.title || 'Never miss a message'}
                                </Txt>
                                <Txt fontSize={14} color="gray.600" mt={1}>
                                    {notification.text}
                                </Txt>
                            </VStack>
                        </HStack>
                        <Pressable onPress={handleDismiss} p={1}>
                            <SpIcon icon={faXmark} size={24} color="gray.400" />
                        </Pressable>
                    </HStack>

                    <VStack space={2}>
                        <Txt fontSize={14} fontWeight="500" color="gray.900">
                            Email Address
                        </Txt>
                        <Input
                            value={localEmail}
                            onChangeText={setLocalEmail}
                            placeholder="Enter your email"
                            keyboardType="email-address"
                            autoCapitalize="none"
                            autoCorrect={false}
                            borderWidth={1}
                            borderColor="gray.300"
                            rounded="md"
                            px={4}
                            py={3}
                            bg="white"
                            fontSize={15}
                            color="gray.700"
                        />
                    </VStack>

                    <Button
                        text="Send Email Confirmation"
                        onPress={handleSendConfirmation}
                        loading={isAccepting}
                        variant="button"
                    />

                    <Txt fontSize={12} color="gray.600" textAlign="center">
                        After clicking, check your inbox for a verification email. The link will expire in 3 days. You can unsubscribe from marketing communications at any time.
                    </Txt>
                </VStack>
            </Box>
        </>
    );
};

// Default fallback card
const DefaultNotificationCard = ({
    notification,
    onAccept,
    onDelete,
    isAccepting,
}: NotificationCardProps) => {

    return (
        <Box
            bg="white"
            borderBottomWidth={1}
            borderBottomColor="gray.200"
            px={4}
            py={5}
        >
            <HStack space={3} alignItems="flex-start" justifyContent="space-between">
                <VStack flex={1} space={2}>
                    <Txt fontSize={18} fontWeight="600" color="gray.900">
                        {notification.title || 'Notification'}
                    </Txt>
                    <Txt fontSize={14} color="gray.700">
                        {notification.text}
                    </Txt>
                    <Button
                        text="Accept"
                        onPress={() => onAccept(notification.id)}
                        loading={isAccepting}
                        variant="button"
                    />
                </VStack>
                <Pressable onPress={() => onDelete(notification.id)} p={1}>
                    <SpIcon icon={faXmark} size={24} color="gray.400" />
                </Pressable>
            </HStack>
        </Box>
    );
};

export default NotificationCard;
